import { ENTITY_STATUS } from '@monorepo/shared/schemas/common'
import type { ColumnDef } from '@tanstack/react-table'
import { Edit, MoreHorizontal, Pause, Play, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { useDeleteAgent, useUpdateAgent } from '../hooks/useAgents'
import type { Agent } from '../types/agent.types'

interface AgentsTableProps {
  agents: Agent[]
  onEditAgent?: (agent: Agent) => void
  onViewAgent?: (agent: Agent) => void
}

export function AgentsTable({
  agents,
  onEditAgent,
  onViewAgent,
}: AgentsTableProps) {
  const [deleteAgent, setDeleteAgent] = useState<Agent | null>(null)
  const deleteAgentMutation = useDeleteAgent()
  const updateAgentMutation = useUpdateAgent()

  const handleAgentAction = async (action: string, agent: Agent) => {
    switch (action) {
      case 'view': {
        onViewAgent?.(agent)
        break
      }
      case 'edit': {
        onEditAgent?.(agent)
        break
      }
      case 'activate': {
        try {
          await updateAgentMutation.mutateAsync({
            id: agent.id,
            data: { status: ENTITY_STATUS.active },
          })
        } catch (error) {
          toast.error(extractErrorMessage(error))
        }
        break
      }
      case 'deactivate': {
        try {
          await updateAgentMutation.mutateAsync({
            id: agent.id,
            data: { status: ENTITY_STATUS.draft },
          })
        } catch (error) {
          toast.error(extractErrorMessage(error))
        }
        break
      }
      case 'delete': {
        setDeleteAgent(agent)
        break
      }
    }
  }

  const handleConfirmDelete = async () => {
    if (deleteAgent) {
      try {
        await deleteAgentMutation.mutateAsync(deleteAgent.id)
        setDeleteAgent(null)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      }
    }
  }

  const columns: ColumnDef<Agent>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const agent = row.original
        return <div className='font-medium'>{agent.name}</div>
      },
      size: 200,
    },
    {
      accessorKey: 'external_id',
      header: 'External ID',
      cell: ({ row }) => {
        const agent = row.original
        return (
          <div className='font-mono text-sm'>{agent.external_id || '-'}</div>
        )
      },
      size: 150,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const agent = row.original
        return (
          <Badge
            variant={
              agent.status === ENTITY_STATUS.active ? 'default' : 'secondary'
            }
            className='text-xs'
          >
            {agent.status}
          </Badge>
        )
      },
      size: 100,
    },
    {
      accessorKey: 'billableSignals',
      header: 'Billable Signals',
      cell: ({ row }) => {
        const agent = row.original
        return (
          <div className='text-center'>
            <span className='font-medium text-green-600'>
              {agent.billableSignalsCount || 0}
            </span>
          </div>
        )
      },
      size: 120,
    },
    {
      accessorKey: 'costSignals',
      header: 'Cost Signals',
      cell: ({ row }) => {
        const agent = row.original
        return (
          <div className='text-center'>
            <span className='font-medium text-red-600'>
              {agent.costSignalsCount || 0}
            </span>
          </div>
        )
      },
      size: 120,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const agent = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm'>
                <MoreHorizontal className='w-4 h-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  handleAgentAction('edit', agent)
                }}
              >
                <Edit className='w-4 h-4 mr-2' />
                Edit
              </DropdownMenuItem>
              {agent.status === ENTITY_STATUS.draft && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleAgentAction('activate', agent)
                  }}
                >
                  <Play className='w-4 h-4 mr-2' />
                  Activate
                </DropdownMenuItem>
              )}
              {agent.status === ENTITY_STATUS.active && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleAgentAction('deactivate', agent)
                  }}
                >
                  <Pause className='w-4 h-4 mr-2' />
                  Make Draft
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  handleAgentAction('delete', agent)
                }}
                className='text-red-600 focus:text-red-600'
              >
                <Trash2 className='w-4 h-4 mr-2' />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      size: 50,
    },
  ]

  return (
    <>
      <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
        <EnhancedTable
          key='agents-table-status-3rd-position'
          data={agents}
          columns={columns}
          height='calc(100vh - 400px)'
          className='bg-transparent border-0'
          onRowClick={agent => onViewAgent?.(agent)}
        />
      </div>

      <ConfirmationDialog
        open={!!deleteAgent}
        onOpenChange={open => !open && setDeleteAgent(null)}
        title='Delete Agent'
        description={`Are you sure you want to delete "${deleteAgent?.name}"? This action cannot be undone and will permanently remove the agent and all its associated data.`}
        confirmText='Delete'
        cancelText='Cancel'
        variant='destructive'
        isLoading={deleteAgentMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteAgent(null)}
      />
    </>
  )
}
